import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AccessDeniedCard from './AccessDeniedCard';

interface EvaluatorRouteProps {
  children: React.ReactNode;
}

function normalizeRole(raw: string | undefined | null): string | null {
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

// feat-191-evaluator-role (C1 / ADR 0001 §A-9).
//
// Admits Bespoke Admin OR the `evaluator` role, and nobody else. Applied to
// EXACTLY the three `/restaurant/internal/interaction-evaluations*` routes —
// the entire evaluator surface (D3). Its siblings `/restaurant/internal`,
// `/restaurant/internal/timeline` and `/restaurant/internal/pilot-trends` stay
// <AdminRoute>: an evaluator must not reach the Bespoke Admin hub, the PIT
// timeline, or Pilot Trends.
//
// This is an ALLOWLIST, exactly like AdminRoute and StaffRoute — any role that
// is neither Admin nor Evaluator falls through to "Access denied". Do NOT add
// `evaluator` to StaffRoute or to src/utils/permissions.ts to "make this work":
// those two denying an evaluator IS the feature (AC4, AC7).
const EvaluatorRoute: React.FC<EvaluatorRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral mx-auto"></div>
          <p className="mt-4 text-inkMuted">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user || !user.isAuthenticated) {
    return <Navigate to="/scorecard-login" replace />;
  }

  const role = normalizeRole(user.managementUser?.role);
  if (role !== 'Admin' && role !== 'Evaluator') {
    // feat-324: shared card — the denial (Variant B) or, when the role lookup
    // FAILED, "We couldn't load your account" (Variant A). Both offer Sign out.
    return <AccessDeniedCard message="This page is only available to Bespoke administrators and evaluators." />;
  }

  return <>{children}</>;
};

export default EvaluatorRoute;
